import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useTicketNotificationsCount } from '@/hooks/useTicketNotifications';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  LayoutDashboard,
  Users,
  Settings,
  UserCog,
  BarChart3,
  MessageSquare,
  LogOut,
  Send,
  DollarSign,
  Zap,
  Heart,
  FileText,
  Briefcase,
  Calendar,
  Headphones,
  User,
  ScrollText,
  Receipt,
  Mail
} from 'lucide-react';
import { ACCESS_AREAS } from '@/constants/accessAreas';

const AppSidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { count: unviewedCount, hasNotifications, refresh: refreshNotifications } = useTicketNotificationsCount();

  const superAdminItems = [
    {
      title: "Dashboard",
      url: "/dashboard",
      icon: LayoutDashboard,
    },
    {
      title: "Cronograma de Envios",
      url: "/schedule",
      icon: Calendar,
    },
    {
      title: "CRM / Vendas",
      url: "/sales",
      icon: Briefcase,
    },
    {
      title: "Config. Comerciais",
      url: "/settings/commercial",
      icon: Settings,
    },
    {
      title: "Implantação",
      url: "/customer-support",
      icon: Headphones,
    },
    {
      title: "Tickets",
      url: "/tickets",
      icon: FileText,
    },
    {
      title: "Clientes",
      url: "/clients",
      icon: Users,
    },
    {
      title: "Usuários",
      url: "/users",
      icon: UserCog,
    },
    {
      title: "Configurações",
      url: "/settings",
      icon: Settings,
    },
    {
      title: "Log de Auditoria",
      url: "/audit-logs",
      icon: ScrollText,
    },
    {
      title: "Central de Cobrança",
      url: "/central-cobranca",
      icon: Receipt,
    },
    {
      title: "Lotes de envio",
      url: "/disparos",
      icon: DollarSign,
    },
    {
      title: "Disparo Avulso",
      url: "/disparo-avulso",
      icon: Send,
    },
    {
      title: "Email",
      url: "/email",
      icon: Mail,
    },
    {
      title: "Gestão de Atendimento",
      url: "/attendances",
      icon: BarChart3,
    },
    // {
    //   title: "Campanhas Integradas",
    //   url: "/campanhas-integradas",
    //   icon: Zap,
    // },
    // {
    //   title: "Monitoramento Filas",
    //   url: "/queues-monitoring",
    //   icon: BarChart3,
    // },
    // {
    //   title: "Sucesso do Cliente",
    //   url: "/client-success",
    //   icon: Heart,
    // },
  ];

  const clientAdminItems = [
    {
      title: "Dashboard",
      url: "/dashboard",
      icon: LayoutDashboard,
    },
    {
      title: "Cronograma de Envios",
      url: "/client-schedule",
      icon: Calendar,
    },
    {
      title: "Regras de Negócio",
      url: "/client-business-rules",
      icon: Settings,
    },
    {
      title: "Lotes de envio",
      url: "/client-disparos",
      icon: DollarSign,
    },
    {
      title: "Disparo Avulso",
      url: "/disparo-avulso",
      icon: Send,
    },
    {
      title: "Email",
      url: "/email",
      icon: Mail,
    },
    {
      title: "Log de Auditoria",
      url: "/audit-logs",
      icon: ScrollText,
    },
    {
      title: "Gestão de Atendimento",
      url: "/attendances",
      icon: BarChart3,
    },
  ];

  const clientVisualItems = [
    { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
    { title: "Cronograma de Envios", url: "/client-schedule", icon: Calendar },
    { title: "Disparo Avulso", url: "/disparo-avulso", icon: Send },
    { title: "Email", url: "/email", icon: Mail },
  ];

  let menuItems: typeof superAdminItems;
  if (user?.role === 'admin') {
    menuItems = superAdminItems;
  } else if (user?.role === 'cliente_visual') {
    menuItems = clientVisualItems;
  } else {
    menuItems = clientAdminItems;
  }

  // Filtrar menus se for analista (baseado nos itens de admin)
  if (user?.role === 'analista') {
    const allowed = user.allowed_areas || [];
    console.log('🔍 [AppSidebar] Filtrando menus para analista:', {
      allowed_areas: allowed,
      superAdminItems: superAdminItems.map(i => i.url)
    });
    menuItems = superAdminItems.filter(item => allowed.includes(item.url));
    console.log('🔍 [AppSidebar] Menus filtrados:', menuItems.map(i => i.title));
  }

  // Debug: verificar se o menu está correto
  React.useEffect(() => {
    console.log('🔍 [AppSidebar] Informações do usuário:', {
      role: user?.role,
      totalMenuItems: menuItems.length,
      menuTitles: menuItems.map(item => item.title)
    });
  }, [user, menuItems]);

  const isActive = (url: string) => {
    return location.pathname === url;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Sidebar className="border-r border-sidebar-border bg-sidebar">
      <SidebarHeader className="p-4 border-b border-sidebar-border/50 bg-sidebar">
      </SidebarHeader>

      <SidebarContent className="px-4 py-6">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {menuItems.map((item) => {
                const active = isActive(item.url);
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      className="w-full h-auto p-0 hover:bg-transparent"
                    >
                      <Link
                        to={item.url}
                        className={`
                          flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200
                          ${active
                            ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                            : 'text-sidebar-foreground hover:bg-sidebar-accent/50'
                          }
                        `}
                      >
                        <item.icon className={`
                          h-4 w-4 flex-shrink-0
                          ${active ? 'text-sidebar-accent-foreground' : 'text-sidebar-foreground/70'}
                        `} />
                        <span className="text-sm flex-1">{item.title}</span>
                        {item.title === 'Tickets' && hasNotifications && (
                          <Badge
                            variant="destructive"
                            className="h-5 min-w-5 flex items-center justify-center px-1.5 text-xs font-semibold"
                          >
                            {unviewedCount > 99 ? '99+' : unviewedCount}
                          </Badge>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-4 border-t border-sidebar-border/50">
        <div className="space-y-3">
          {/* Seção Ajustes */}
          <div className="px-3 py-2">
            <div className="flex items-center gap-2 text-sidebar-foreground/70 mb-3">
              <Settings className="h-4 w-4" />
              <span className="text-xs font-medium">Ajustes</span>
            </div>

            {/* Informações do Usuário */}
            <div className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-sidebar-accent/50 transition-colors">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <User className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-sidebar-foreground truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                {user?.client?.legal_name && (
                  <p className="text-xs text-primary font-medium mt-0.5 truncate">{user.client.legal_name}</p>
                )}
              </div>
            </div>
          </div>

          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full justify-start text-sidebar-foreground/70 hover:text-destructive hover:bg-destructive/10"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};

export default AppSidebar;
